import { buildConfirmationFilename, CompanyGuid, MaintenancePlanGuid } from '@breezy/shared'
import { useCallback, useRef, useState } from 'react'
import { getConfig } from '../../config'
import { useDownloadBlob } from '../../hooks/blob-hooks'
import { useMessage } from '../../utils/antd-utils'

const config = getConfig()

/**
 * Customer-portal download of the Maintenance Plan confirmation PDF.
 *
 * Hits the unauth Express route
 * `POST /unauth/maintenance-plan-confirmation-pdf/:companyGuid/:planGuid`.
 *
 * Checks `response.ok` before decoding — diverging from the buggy invoice
 * unauth helper at `invoice-utils.ts:11-30` which never checks status.
 */
export const useDownloadMaintenancePlanConfirmationUnauth = ({
  companyGuid,
  maintenancePlanGuid,
  planName,
}: {
  companyGuid: CompanyGuid
  maintenancePlanGuid: MaintenancePlanGuid
  planName: string
}) => {
  const message = useMessage()
  const downloadBlob = useDownloadBlob()
  const [isDownloading, setIsDownloading] = useState(false)
  const isDownloadingRef = useRef(false)

  const downloadConfirmation = useCallback(async () => {
    if (isDownloadingRef.current) return false

    isDownloadingRef.current = true

    try {
      return await downloadBlob({
        getBlob: async () => {
          const response = await fetch(
            `${config.apiUrl}/unauth/maintenance-plan-confirmation-pdf/${companyGuid}/${maintenancePlanGuid}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            },
          )
          if (!response.ok) {
            throw new Error(`Download failed with status ${response.status}`)
          }
          const arrayBuffer = await response.arrayBuffer()
          return new Blob([arrayBuffer], { type: 'application/pdf' })
        },
        downloadFilename: buildConfirmationFilename(planName, maintenancePlanGuid),
        operationText: 'Download Plan Confirmation',
        onStarted: () => {
          setIsDownloading(true)
          message.info('Downloading plan confirmation...')
        },
        onFinished: () => {
          isDownloadingRef.current = false
          setIsDownloading(false)
        },
      })
    } catch (error) {
      isDownloadingRef.current = false
      setIsDownloading(false)
      throw error
    }
  }, [companyGuid, downloadBlob, maintenancePlanGuid, message, planName])

  return { downloadConfirmation, isDownloading }
}
